import React, { useState } from 'react';
import {
  TextField,
  Button,
  Box,
  Typography,
  Paper,
  Stack,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import api from '../api/axios';
import ChangePasswordPage from './ChangePasswordPage';
import { useDarkMode } from '../App';

function LoginPage({ onLogin }) {
  const { darkMode } = useDarkMode();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [msg, setMsg] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [needPasswordChange, setNeedPasswordChange] = useState(false);
  const [tempUsername, setTempUsername] = useState('');

const handleSubmit = async (e) => {
  e.preventDefault();
  setMsg('');

  const errors = {};
  if (!username.trim()) errors.username = '아이디를 입력해 주세요';
  if (!password) errors.password = '비밀번호를 입력해 주세요';
  setFieldErrors(errors);
  if (Object.keys(errors).length > 0) return;

  setLoading(true);

  try {
    const res = await api.post(
      '/auth/login',
      { username, password, rememberMe: true },
      { withCredentials: true }
    );

    if (res.data.needPasswordChange) {
      setTempUsername(username);
      setNeedPasswordChange(true);
    } else {
      onLogin(res.data.user);
    }
  } catch (err) {
    setMsg(err?.response?.data?.message || '로그인 실패');
  } finally {
    setLoading(false);
  }
};


  const handlePasswordChanged = (user) => {
    setNeedPasswordChange(false);
    onLogin(user);
  };

  // 비밀번호 변경 화면 표시
  if (needPasswordChange) {
    return (
      <ChangePasswordPage
        username={tempUsername}
        onPasswordChanged={handlePasswordChanged}
      />
    );
  }

  // 일반 로그인 화면
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: darkMode
          ? 'linear-gradient(180deg, #121212 0%, #1e1e1e 100%)'
          : 'linear-gradient(180deg, #fefefe 0%, #fff9f2 100%)',
        px: 2
      }}
    >
      <Paper
        elevation={6}
        sx={{
          p: { xs: 3, sm: 6 },
          borderRadius: 3,
          width: { xs: '100%', sm: 450 },
          maxWidth: '100%',
          textAlign: 'center',
          position: 'relative',
          bgcolor: 'background.paper',
          boxShadow: darkMode
            ? '0px 8px 20px rgba(0,0,0,0.4)'
            : '0px 8px 20px rgba(0,0,0,0.1)'
        }}
      >
        {/* 로고/타이틀 */}
        <Typography variant="h4" fontWeight="bold" mb={3} sx={{ fontFamily: 'serif', color: 'text.primary' }}>
          Piano Woollim
        </Typography>

        {msg && (
          <Typography color="error" variant="body2" mb={2}>
            {msg}
          </Typography>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="아이디"
              name="username"
              autoComplete="username"
              slotProps={{ htmlInput: { autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false } }}
              value={username}
              onChange={e => { setUsername(e.target.value); setFieldErrors(prev => ({ ...prev, username: undefined })); }}
              required
              error={!!fieldErrors.username}
              helperText={fieldErrors.username}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: '#d4af37', // 골드 포인트
                  },
                },
              }}
            />

            <TextField
              fullWidth
              type={showPassword ? "text" : "password"}
              label="비밀번호"
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={e => { setPassword(e.target.value); setFieldErrors(prev => ({ ...prev, password: undefined })); }}
              required
              error={!!fieldErrors.password}
              helperText={fieldErrors.password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(prev => !prev)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: '#d4af37',
                  },
                },
              }}
            />

            <Button
              fullWidth
              type="submit"
              size="large"
              variant="contained"
              disabled={loading}
              sx={{
                background: 'linear-gradient(90deg, #222 0%, #555 100%)',
                color: '#f5e1a4',
                fontWeight: 'bold',
                textTransform: 'none',
                py: 1.8,
                borderRadius: 2,
                fontSize: '1rem',
                '&:hover': {
                  background: 'linear-gradient(90deg, #555 0%, #222 100%)',
                }
              }}
            >
              {loading ? "로그인 중..." : "로그인"}
            </Button>
          </Stack>
        </form>

      </Paper>
    </Box>
  );
}

export default LoginPage;